import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Sparkles,
  Compass,
  Package,
  Plug,
  ListTodo,
  ArrowRight,
  PartyPopper,
  LogOut,
} from "lucide-react";
import { useCompanyId } from "@/lib/CompanyContext";
import { authApi } from "@/lib/auth";
import { SupportWidget } from "@/components/SupportWidget";
import {
  useSetupProgress,
  type SetupStepKey,
} from "@/lib/useSetupProgress";

/**
 * Primeiros passos — onboarding guiado pra conta nova. Tela cheia, sem menu
 * lateral (montada fora do Shell), pra o usuário não se sentir perdido. Mostra
 * uma trilha de passos com estado ao vivo (✓ quando feito) e um botão pra ir
 * fazer cada um. Enquanto o onboarding não estiver feito, o app redireciona
 * pra cá; depois disso o usuário navega livre e essa página fica acessível
 * pelo atalho no menu até completar tudo.
 */

type StepDef = {
  key: SetupStepKey;
  icon: typeof Sparkles;
  title: string;
  desc: string;
  cta: string;
  /** rota fixa, ou função que recebe o id do 1º produto. */
  route: string | ((firstProductId: string | null) => string | null);
};

const STEP_DEFS: StepDef[] = [
  {
    key: "onboarding",
    icon: Sparkles,
    title: "Faça a Base de Conhecimento Onboarding",
    desc: "O onboarding ensina a IA quem você é: seu tom de voz, posicionamento, o que você vende, o que evitar, e a identidade visual da marca. É a base de tudo que ela vai criar — inclusive do diagnóstico ESCALA e das pesquisas que vêm depois.",
    cta: "Fazer o onboarding",
    route: "/briefing/onboarding",
  },
  {
    key: "escala",
    icon: Compass,
    title: "Faça o diagnóstico ESCALA",
    desc: "Responda o formulário e em poucos minutos você terá seu diagnóstico estratégico com uma visão 360º do seu negócio de acordo com os pilares da metodologia ESCALA.",
    cta: "Fazer o diagnóstico",
    route: "/escala/wizard",
  },
  {
    key: "produto",
    icon: Package,
    title: "Cadastre seu primeiro produto ou oferta",
    desc: "Crie a base de conhecimento do seu produto ou da sua oferta principal para direcionar a inteligência do Mohdus sobre o que é o seu produto, para quem é, qual é a promessa e o preço. Você pode cadastrar mais de um produto ou oferta.",
    cta: "Criar produto",
    route: "/briefing/nova?type=product",
  },
  {
    key: "integracao",
    icon: Plug,
    title: "Conecte uma ferramenta (opcional)",
    desc: "Conecte sua conta de anúncios do META ADS. Com essa conexão os agentes do Mohdus são capazes de ler os dados em tempo real e te dar uma visão clara da performance das suas campanhas.\n\nCaso queira, esse processo pode ser feito em outro momento.",
    cta: "Ver integrações",
    route: "/integracoes",
  },
  {
    key: "tarefa",
    icon: ListTodo,
    title: "Crie sua primeira tarefa",
    desc: "Peça um post, um carrossel, uma análise dos seus anúncios... A IA monta o entregável e você revisa, ajusta e aprova.",
    cta: "Criar tarefa",
    route: "/nova-tarefa",
  },
];

const STEP_BLOCKED_HINT: Record<string, string> = {
  produto: "Crie um produto primeiro",
  onboarding: "Faça o onboarding primeiro",
};

export default function PrimeirosPassos() {
  const navigate = useNavigate();
  const companyId = useCompanyId();
  const progress = useSetupProgress(companyId);
  const queryClient = useQueryClient();
  const signOut = useMutation({
    mutationFn: () => authApi.signOut(),
    onSuccess: () => {
      queryClient.clear();
      navigate("/auth", { replace: true });
    },
  });

  const goStep = (def: StepDef) => {
    const route =
      typeof def.route === "function" ? def.route(progress.firstProductId) : def.route;
    if (route) navigate(route);
  };

  const stepState = (key: SetupStepKey) => progress.steps.find((s) => s.key === key);

  // Próximo passo a fazer = primeiro required não-feito e não-bloqueado.
  const nextKey = progress.steps.find(
    (s) => !s.optional && !s.done && !s.blockedBy,
  )?.key;

  return (
    <div
      className="wf"
      style={{
        minHeight: "100vh",
        height: "auto",
        overflow: "auto",
        display: "flex",
        justifyContent: "center",
        padding: "48px 20px 80px",
      }}
    >
      {/* Logout — pra não ficar preso se o onboarding não foi feito */}
      <button
        type="button"
        onClick={() => signOut.mutate()}
        disabled={signOut.isPending}
        className="wf-btn wf-btn--ghost wf-btn--sm"
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 20,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <LogOut size={14} strokeWidth={2} />
        {signOut.isPending ? "Saindo…" : "Sair"}
      </button>
      <div style={{ width: "100%", maxWidth: 660 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img
            src="/mohdus-logo.png"
            alt="Mohdus"
            style={{ width: 160, margin: "0 auto 20px", display: "block" }}
          />
          <h1
            className="wf-display"
            style={{
              fontSize: 28,
              fontWeight: 800,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              margin: 0,
            }}
          >
            Vamos preparar a sua Nova Dimensão
          </h1>
          <p
            style={{
              fontSize: 15,
              color: "var(--wf-ink-2)",
              marginTop: 10,
              lineHeight: 1.5,
            }}
          >
            Quanto melhor o contexto que você dá, melhor a IA trabalha pra você.
            São {progress.requiredTotal || 5} passos — leva uns minutos.
          </p>
        </div>

        {/* Progress bar */}
        {!progress.loading && (
          <div
            className="wf-card"
            style={{
              padding: "14px 18px",
              marginBottom: 22,
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  height: 8,
                  borderRadius: 999,
                  background: "var(--wf-ink-5)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${progress.requiredTotal ? (progress.requiredDone / progress.requiredTotal) * 100 : 0}%`,
                    background: "var(--nd-orange)",
                    borderRadius: 999,
                    transition: "width 300ms",
                  }}
                />
              </div>
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--wf-ink-2)", whiteSpace: "nowrap" }}>
              {progress.requiredDone} de {progress.requiredTotal} concluídos
            </span>
          </div>
        )}

        {/* All done */}
        {progress.allRequiredDone && (
          <div
            className="wf-card"
            style={{
              padding: 20,
              marginBottom: 22,
              border: "1px solid var(--nd-orange)",
              background: "rgba(254,112,0,0.06)",
              display: "flex",
              gap: 14,
              alignItems: "center",
            }}
          >
            <PartyPopper size={26} color="var(--nd-orange)" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div className="wf-display" style={{ fontSize: 16, fontWeight: 700 }}>
                Tudo pronto!
              </div>
              <div style={{ fontSize: 13, color: "var(--wf-ink-2)", marginTop: 2 }}>
                Você já configurou o essencial. Agora é só usar — e quanto mais
                você aprova entregáveis, mais a IA aprende o seu jeito.
              </div>
            </div>
            <button
              type="button"
              className="wf-btn wf-btn--primary"
              onClick={() => navigate("/painel")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}
            >
              Ir pro painel <ArrowRight size={14} />
            </button>
          </div>
        )}

        {/* Steps */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {STEP_DEFS.map((def, i) => {
            const st = stepState(def.key);
            const done = !!st?.done;
            const blocked = !done && !!st?.blockedBy;
            const isNext = def.key === nextKey;
            const StepIcon = def.icon;
            return (
              <div
                key={def.key}
                className="wf-card"
                style={{
                  padding: 18,
                  display: "flex",
                  gap: 14,
                  alignItems: "flex-start",
                  border: isNext ? "1px solid var(--nd-orange)" : undefined,
                  background: isNext ? "rgba(254,112,0,0.04)" : undefined,
                }}
              >
                {/* Number / check */}
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 13,
                    background: done
                      ? "var(--nd-orange)"
                      : isNext
                        ? "var(--nd-orange)"
                        : "var(--wf-ink-5)",
                    color: done || isNext ? "#fff" : "var(--wf-ink-2)",
                  }}
                >
                  {done ? <Check size={16} strokeWidth={3} /> : i + 1}
                </div>

                <div style={{ flex: 1, minWidth: 0, opacity: done ? 0.62 : 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      fontWeight: 700,
                      fontSize: 14.5,
                      color: "var(--wf-ink)",
                    }}
                  >
                    <StepIcon size={15} color="var(--wf-ink-3)" />
                    {def.title}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--wf-ink-2)",
                      marginTop: 4,
                      lineHeight: 1.5,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {def.desc}
                  </div>
                  {blocked && (
                    <div style={{ fontSize: 12, color: "var(--wf-ink-3)", marginTop: 6 }}>
                      {STEP_BLOCKED_HINT[st!.blockedBy!] ?? "Faça os passos anteriores primeiro"}
                    </div>
                  )}
                </div>

                {/* Passo concluído continua clicável (botão "Revisar") pra o
                    usuário reabrir e ver/ajustar — ex: voltar semanas depois e
                    olhar o onboarding. Antes o botão sumia ao concluir e não
                    havia como revisitar. */}
                {def.route && (
                  <button
                    type="button"
                    className={`wf-btn ${!done && isNext ? "wf-btn--primary" : "wf-btn--ghost"} wf-btn--sm`}
                    onClick={() => goStep(def)}
                    disabled={!done && blocked}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      opacity: !done && blocked ? 0.5 : 1,
                      cursor: !done && blocked ? "not-allowed" : "pointer",
                    }}
                  >
                    {done ? "Revisar" : def.cta} <ArrowRight size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Sem "pular pro app": o painel só desbloqueia depois de TODOS os passos
            obrigatórios (regra do OnboardingGate). O botão de ir pro painel só
            aparece no estado "tudo concluído" (acima). */}
      </div>
      <SupportWidget />
    </div>
  );
}
